import { useState, useEffect } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Database } from "@/types/supabase";

const languageFlags = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ar: "🇸🇦"
};

type Category = Database['public']['Tables']['categories']['Row'];

export default function NewCategory() {
  const [title, setTitle] = useState({
    tr: "",
    en: "",
    ar: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nextOrderNum, setNextOrderNum] = useState(1);
  
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Mevcut en yüksek sıra numarasını bul
  useEffect(() => {
    async function fetchMaxOrderNum() {
      try {
        // Tüm kategorileri getir
        const { data, error } = await supabase
          .from('categories')
          .select('order_num')
          .order('order_num', { ascending: false })
          .limit(1);
        
        if (error) {
          console.error('Kategori sıra numarası alınamadı:', error);
          return;
        }
        
        // En son sıra numarasını al ve 1 arttır
        if (data && data.length > 0 && data[0].order_num) {
          setNextOrderNum(data[0].order_num + 1);
        }
      } catch (error) {
        console.error('Sıra numarası hesaplanırken hata oluştu:', error);
      }
    }
    
    fetchMaxOrderNum();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const newCategory: Database['public']['Tables']['categories']['Insert'] = {
        name_tr: title.tr,
        name_en: title.en,
        name_ar: title.ar,
        order_num: nextOrderNum
      };

      const { error } = await supabase
        .from('categories')
        .insert(newCategory);
      
      if (error) throw error;
      
      toast({
        title: "Başarılı",
        description: "Kategori başarıyla oluşturuldu.",
      });
      
      navigate("/portfolio/categories");
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: "Hata",
        description: "Kategori oluşturulurken bir hata oluştu.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentLayout 
      title="Yeni Kategori" 
      backLink="/portfolio/categories"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <Tabs defaultValue="tr" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="tr">{languageFlags.tr} Türkçe</TabsTrigger>
            <TabsTrigger value="en">{languageFlags.en} English</TabsTrigger>
            <TabsTrigger value="ar">{languageFlags.ar} العربية</TabsTrigger>
          </TabsList>
          <TabsContent value="tr" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title-tr">Kategori Adı (Türkçe)</Label>
              <Input
                id="title-tr"
                placeholder="Kategori adını girin"
                value={title.tr}
                onChange={(e) => setTitle({ ...title, tr: e.target.value })}
                required
              />
            </div>
          </TabsContent>
          <TabsContent value="en" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title-en">Kategori Adı (English)</Label>
              <Input
                id="title-en"
                placeholder="Enter category name"
                value={title.en}
                onChange={(e) => setTitle({ ...title, en: e.target.value })}
                required
              />
            </div>
          </TabsContent>
          <TabsContent value="ar" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title-ar">Kategori Adı (العربية)</Label>
              <Input
                id="title-ar"
                placeholder="أدخل اسم الفئة"
                value={title.ar}
                onChange={(e) => setTitle({ ...title, ar: e.target.value })}
                required
              />
            </div>
          </TabsContent>
        </Tabs>
        
        <div className="flex justify-end gap-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate("/portfolio/categories")}
          >
            İptal
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </form>
    </ContentLayout>
  );
}
